import * as Sentry from '@sentry/react-native';
import Config from 'react-native-config';
import { TelemetryContext, TelemetryLevel, UserContext } from './types';

class TelemetryService {
  private static instance: TelemetryService;
  private sentryInitialized = false;
  private eventCache = new Set<string>();

  private constructor() { }

  public static getInstance(): TelemetryService {
    if (!TelemetryService.instance) {
      TelemetryService.instance = new TelemetryService();
    }
    return TelemetryService.instance;
  }

  public init(): void {
    if (this.sentryInitialized) return;

    if (Config.SENTRY_ENABLED !== 'true') {
      return;
    }

    Sentry.init({
      dsn: Config.SENTRY_DSN,
      environment: Config.ENVIRONMENT,
      tracesSampleRate: 1.0,
      tunnel: Config.API_URL + "/rpm/api/sentry",
      enableLogs: true,
      debug: Config.ENVIRONMENT !== 'production',
    });
    this.sentryInitialized = true;

  }

  public setUser(user: UserContext | null): void {
    if (!user) {
      Sentry.setUser(null);
      return;
    }
    Sentry.setUser({
      id: user.id,
    });
  }

  public setContext(key: string, value: Record<string, unknown>): void {
    Sentry.setContext(key, value);
  }

  public breadcrumb(message: string, data?: Record<string, unknown>, level: TelemetryLevel = 'info'): void {
    if (Config.ENVIRONMENT !== 'production') {
      console.log(`[Telemetry Breadcrumb] ${message}`, data);
    }
    Sentry.addBreadcrumb({
      message,
      data,
      level: level as Sentry.SeverityLevel,
    });
  }

  public log(level: TelemetryLevel, message: string, context?: Record<string, unknown>): void {
    const sanitizedCtx = this.sanitize(context);

    if (Config.ENVIRONMENT !== 'production') {
      console.log(`[Telemetry Log] ${message} (${level})`, sanitizedCtx);
    }

    const sentryLogger = Sentry.logger;

    if (sentryLogger) {
      if (Config.ENVIRONMENT !== 'production') {
        console.log("Logging to Sentry:", level, message, sanitizedCtx);
      }
      switch (level) {
        case 'debug': sentryLogger.debug(message, sanitizedCtx); break;
        case 'info': sentryLogger.info(message, sanitizedCtx); break;
        case 'warning': sentryLogger.warn(message, sanitizedCtx); break;
        case 'error': sentryLogger.error(message, sanitizedCtx); break;
        case 'fatal': sentryLogger.fatal(message, sanitizedCtx); break;
        default: sentryLogger.info(message, sanitizedCtx);
      }
    }
  }

  public event(name: string, props?: TelemetryContext, level: TelemetryLevel = 'info'): void {
    const sanitizedProps = this.sanitize(props);

    if (Config.ENVIRONMENT !== 'production') {
      console.log(`[Telemetry Event] ${name} (${level})`, sanitizedProps);
    }

    if (level === 'info' || level === 'debug') {
      if (level === 'debug' && Config.ENVIRONMENT === 'production') {
        return; 
      }
    }

    Sentry.captureMessage(name, {
      level: level as Sentry.SeverityLevel,
      tags: sanitizedProps ? this.extractTags(sanitizedProps) : undefined,
      contexts: sanitizedProps ? { data: sanitizedProps } : undefined,
    });
  }

  public eventOnce(name: string, props?: TelemetryContext, level: TelemetryLevel = 'warning'): void {
    if (this.eventCache.has(name)) {
      return;
    }
    this.eventCache.add(name);
    this.event(name, props, level);
  }

  public error(error: unknown, ctx?: TelemetryContext): void {
    const sanitizedCtx = this.sanitize(ctx);

    if (Config.ENVIRONMENT !== 'production') {
      console.error(`[Telemetry Error]`, error, sanitizedCtx);
    }

    Sentry.captureException(error, {
      tags: sanitizedCtx ? this.extractTags(sanitizedCtx) : undefined,
      contexts: sanitizedCtx ? { data: sanitizedCtx } : undefined,
    });
  }

  private sanitize(data?: Record<string, unknown>): Record<string, unknown> | undefined {
    if (!data) return undefined;
    const sanitized = { ...data };
    // Sensitive authentication/security keys
    const sensitiveKeys = ['token', 'password', 'secret', 'authorization', 'auth'];
    // PII (Personally Identifiable Information) keys
    const piiKeys = ['email', 'firstname', 'lastname', 'phone', 'phonenumber', 'taxcode', 'fiscalcode', 'ssn', 'name', 'address', 'birth'];
    
    Object.keys(sanitized).forEach(key => {
      const lowerKey = key.toLowerCase();
      if (sensitiveKeys.some(s => lowerKey.includes(s)) || piiKeys.some(p => lowerKey.includes(p))) {
        delete sanitized[key];
      }
    });
    
    // Deep sanitization for nested objects and arrays
    Object.keys(sanitized).forEach(key => {
      if (sanitized[key] && typeof sanitized[key] === 'object') {
        if (Array.isArray(sanitized[key])) {
          sanitized[key] = '[REDACTED]';
        } else {
          sanitized[key] = this.sanitize(sanitized[key] as Record<string, unknown>);
        }
      }
    });
    
    return sanitized;
  }

  private extractTags(data: Record<string, unknown>): Record<string, string> {
    const tags: Record<string, string> = {};
    const tagKeys = ['domain', 'screen', 'source', 'type', 'reason', 'stage', 'environment', 'release'];

    Object.entries(data).forEach(([key, value]) => {
      if (tagKeys.includes(key) && (typeof value === 'string' || typeof value === 'number')) {
        tags[key] = String(value);
      }
    });

    if (Config.ENVIRONMENT) {
      tags['environment'] = Config.ENVIRONMENT;
    }

    return tags;
  }
}

export const Telemetry = TelemetryService.getInstance();
